import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDemoCtx } from './DemoContext';
import { IrrForm } from './panels/IrrForm';
import {
    getMoistStatus,
    getSiteWeather,
    MOIST_COLOR,
    MOIST_LABEL,
    WEATHER_ICON,
    treeEmoji,
    type MoistStatus,
} from './demoStorage';

type FilterKey = 'all' | MoistStatus;

const FILTERS: { key: FilterKey; label: string }[] = [
    { key: 'all',     label: '전체' },
    { key: 'good',    label: '양호' },
    { key: 'caution', label: '주의' },
    { key: 'warn',    label: '위험' },
    { key: 'danger',  label: '심각' },
];

const STRESS_LABEL: Record<MoistStatus, string> = {
    good:    '낮음',
    caution: '보통',
    warn:    '높음',
    danger:  '심각',
};

function daysAgo(iso: string): number {
    return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

export default function DemoSiteList() {
    const { toast, openPanel, savedSites } = useDemoCtx();
    const navigate = useNavigate();
    const [filter, setFilter]   = useState<FilterKey>('all');
    const [checked, setChecked] = useState<Record<string, boolean>>({});

    const allChk = savedSites.length > 0 && savedSites.every(s => checked[s.id]);
    const chkAll = (v: boolean) => {
        const next: Record<string, boolean> = {};
        savedSites.forEach(s => { next[s.id] = v; });
        setChecked(next);
    };
    const chkOne = (id: string, v: boolean) =>
        setChecked(prev => ({ ...prev, [id]: v }));
    const selectedCnt = Object.values(checked).filter(Boolean).length;

    const filtered = filter === 'all'
        ? savedSites
        : savedSites.filter(s => getMoistStatus(s.moist ?? 50) === filter);

    const openIrr = (name: string, e: React.MouseEvent) => {
        e.stopPropagation();
        openPanel({ title: '관수 등록', body: <IrrForm siteName={name} /> });
    };

    return (
        <div className="list-page">
            {/* Filter bar */}
            <div className="list-bar">
                <div className="chips">
                    {FILTERS.map(f => (
                        <div
                            key={f.key}
                            className={`chip${filter === f.key ? ' active' : ''}`}
                            onClick={() => setFilter(f.key)}
                        >
                            {f.label}
                            <span style={{
                                marginLeft: 4,
                                fontSize: 10,
                                fontWeight: 700,
                                opacity: 0.7,
                            }}>
                                {f.key === 'all'
                                    ? savedSites.length
                                    : savedSites.filter(s => getMoistStatus(s.moist ?? 50) === f.key).length}
                            </span>
                        </div>
                    ))}
                </div>
                <div style={{ flex: 1 }} />
                <button className="btn btn-secondary btn-sm" onClick={() => toast('Excel 내보내기', 'success')}>⬇ Excel</button>
                <button className="btn btn-primary btn-sm" onClick={() => navigate('/demo/sites/add')}>＋ 단지 등록</button>
            </div>

            {/* Context bar */}
            {selectedCnt > 0 && (
                <div className="ctx-bar active">
                    <span className="ctx-count">{selectedCnt}개</span> 선택됨
                    <button className="btn btn-secondary btn-sm" onClick={() => toast('관수 등록', 'success')}>💧 관수 등록</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => toast('Excel 내보내기', 'success')}>⬇ Excel</button>
                    <button className="btn btn-danger btn-sm" onClick={() => { chkAll(false); toast('선택 해제', 'success'); }}>선택 해제</button>
                </div>
            )}

            {/* Table */}
            <div className="tbl-wrap">
                <table className="data-tbl">
                    <thead>
                        <tr>
                            <th><input type="checkbox" checked={allChk} onChange={e => chkAll(e.target.checked)} /></th>
                            <th>상태</th>
                            <th>단지명</th>
                            <th>면적</th>
                            <th>주소</th>
                            <th>주요 수종</th>
                            <th>총 식재수</th>
                            <th>현재 수분율</th>
                            <th>스트레스</th>
                            <th>마지막 관수</th>
                            <th>기온</th>
                            <th>담당자</th>
                            <th>액션</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && (
                            <tr>
                                <td colSpan={13} style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text3)' }}>
                                    해당 상태의 단지가 없습니다
                                </td>
                            </tr>
                        )}
                        {filtered.map(site => {
                            const moist  = site.moist ?? 50;
                            const st     = getMoistStatus(moist);
                            const color  = MOIST_COLOR[st];
                            const days   = daysAgo(site.createdAt);
                            const { temp, weather } = getSiteWeather(site);
                            return (
                                <tr
                                    key={site.id}
                                    style={{ cursor: 'pointer' }}
                                    onClick={() => navigate(`/demo/sites/${encodeURIComponent(site.form.name)}`)}
                                >
                                    <td onClick={e => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={checked[site.id] ?? false}
                                            onChange={e => chkOne(site.id, e.target.checked)}
                                        />
                                    </td>
                                    <td>
                                        <span className={`badge badge-${st}`}>
                                            <span className={`badge-dot dot-${st}`} />
                                            {MOIST_LABEL[st]}
                                        </span>
                                    </td>
                                    <td className="bold">
                                        <div className="site-cell">
                                            <div className="site-thumb" style={{ fontSize: 18 }}>
                                                {treeEmoji(site.form.treeName)}
                                            </div>
                                            {site.form.name || '(미입력)'}
                                        </div>
                                    </td>
                                    <td className="text-mono">{site.form.area || '-'}㎡</td>
                                    <td>{site.form.addr || '-'}</td>
                                    <td>
                                        {site.form.treeName ? (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                background: '#F0FDF4', border: '1px solid #BBF7D0',
                                                borderRadius: 6, padding: '2px 8px',
                                                fontSize: 12, fontWeight: 700, color: '#166534',
                                                whiteSpace: 'nowrap',
                                            }}>
                                                <span style={{ fontSize: 14 }}>{treeEmoji(site.form.treeName)}</span>
                                                {site.form.treeName}
                                            </span>
                                        ) : (
                                            <span style={{ color: 'var(--text3)', fontSize: 12 }}>-</span>
                                        )}
                                    </td>
                                    <td className="text-mono">{site.form.treeCount ? `${site.form.treeCount}주` : '-'}</td>
                                    <td>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <div className="prog">
                                                <div className="prog-fill" style={{ width: `${moist}%`, background: color }} />
                                            </div>
                                            <span className="text-mono" style={{ color, fontWeight: 700 }}>{moist}%</span>
                                        </div>
                                    </td>
                                    <td><span className={`badge badge-${st}`}>{STRESS_LABEL[st]}</span></td>
                                    <td
                                        className="text-mono"
                                        style={{
                                            color: days >= 7 ? 'var(--danger)' : days >= 4 ? 'var(--warn)' : 'var(--text3)',
                                            fontWeight: days >= 7 ? 700 : 400,
                                        }}
                                    >
                                        {days}일전
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>
                                        {WEATHER_ICON[weather]} {temp}°C
                                    </td>
                                    <td>{site.form.mgr || '-'}</td>
                                    <td onClick={e => e.stopPropagation()}>
                                        <div style={{ display: 'flex', gap: 4 }}>
                                            <button
                                                className="btn btn-ghost btn-icon btn-sm"
                                                onClick={() => navigate(`/demo/sites/${encodeURIComponent(site.form.name)}`)}
                                                title="상세보기"
                                            >👁</button>
                                            <button
                                                className="btn btn-ghost btn-icon btn-sm"
                                                onClick={e => openIrr(site.form.name, e)}
                                                title="관수 등록"
                                            >💧</button>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
                {['◀', '1', '2', '3', '▶'].map((p, i) => (
                    <button key={i} className={`pg-btn${p === '1' ? ' active' : ''}`} onClick={() => toast(`${p} 페이지`, 'success')}>{p}</button>
                ))}
            </div>
        </div>
    );
}
